import { useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { ExclamationCircleIcon, Loader } from '@la/ds-ui-components';
import { Team, Tournament } from '@la/types';
import {
  getSubProgramLabelPlural,
  getTeamPriceAdjustmentsMapAndAmounts,
} from '@la/utilities';
import { Alert } from 'components/Alert/Alert';
import ColumnGrid from 'components/ColumnGrid/ColumnGrid';
import ErrorCard from 'components/ErrorCard/ErrorCard';
import useMediaQuery from 'lib/hooks/useMediaQuery';
import { breakpointQueries } from 'lib/media-queries/breakpoints';
import { getLAHostnameParts } from 'lib/utils/urlUtils';
import {
  UpdateRegistrationsPayload,
  useUpdateRegistrationsMutation,
} from 'redux/services/registrationApi';
import { useCreateTeamMutation } from 'redux/services/teamApi';
import MainContent from 'domains/Shell/MainContent/MainContent';
import styles from './Wizard.module.scss';
import {
  RegistrationWizardSliceState,
  getInitialDivisionCardsState,
} from './WizardState';
import { DivisionCards } from './components/DivisionCards/DivisionCards';
import {
  SummaryAndSubmitLargerScreens,
  SummaryAndSubmitSmallerScreens,
} from './components/SummaryAndSubmit/SummaryAndSubmit';
import { WizardHeader } from './components/WizardHeader/WizardHeader';
import { useWizardState } from './hooks/useWizardState';
import * as S from './Wizard.styles';

export const ADD_TO_CART_LOADING_MESSAGE =
  'We are adding your registrations to the cart.';
export const ADD_TO_CART_ERROR_MESSAGE =
  'There was an error adding your registrations. Please try adding again in a few seconds.';

export const UPDATE_CART_LOADING_MESSAGE =
  'We are updating your registrations in the cart.';
export const UPDATE_CART_ERROR_MESSAGE =
  'There was an error updating your registrations. Please try updating again in a few seconds.';
export const SUBMISSION_NO_SELECTED_DIVISIONS_ERROR_MESSAGE =
  'Please add at least one team to register.';
export const DELETE_TEAM_DIVISION_ERROR_MESSAGE =
  'This team cannot be deleted because it is registered for another tournament.';

const getRegistrationsPurgedWarningMessage = (subProgramLabel: string) => (
  <>
    <strong>
      Registration is full for one or more{' '}
      {getSubProgramLabelPlural(subProgramLabel)}.
    </strong>{' '}
    Teams you added have been removed, as spots are filled on a first-come,
    first-served basis. Check back later to see if spots become available!
  </>
);
export type WizardProps = {
  hasRegistrationsToEdit: boolean;
  sectionTitle: string;
  submitButtonText: string;
  submitUnsavedChangesErrorMessage: string;
  teams: Team[];
  tournament: Tournament;
};

/* Wizard */
export function Wizard({
  hasRegistrationsToEdit,
  sectionTitle,
  submitButtonText,
  submitUnsavedChangesErrorMessage,
  teams,
  tournament,
}: WizardProps) {
  const { registrationTriageFix } = useFlags();
  const [createTeam] = useCreateTeamMutation();
  const [hasDeleteTeamDivisionError, setHasDeleteTeamDivisionError] =
    useState(false);
  const [showCartPurgeMessage, setShowCartPurgeMessage] = useState(true);
  const [
    updateRegistrations,
    {
      isLoading: isUpdateRegistrationsLoading,
      isError: updateRegistrationsError,
    },
  ] = useUpdateRegistrationsMutation();
  const [isLoadingCartRegistrations, setIsLoadingCartRegistrations] =
    useState(false);

  const initialWizardState: RegistrationWizardSliceState = {
    divisionCardsState: getInitialDivisionCardsState({
      divisions: tournament.divisions,
      timeZone: tournament.timeZone,
    }),
    submitButtonError: null,
  };

  const {
    submitButtonError,
    divisionCardsState,
    divisionToScrollToId,
    addTeamSelect,
    submit,
    cancelDivisionChanges,
    clearDivision,
    clearIdToScrollTo,
    discardTeamSelect,
    editDivision,
    saveDivision,
    selectDivision,
    selectTeam,
  } = useWizardState({
    initialState: initialWizardState,
  });

  const { sld, subdomain, tld } = getLAHostnameParts();

  const addTeam = async (
    programId: string,
    selectIndex: number,
    team: Omit<Team, 'id' | 'ageGroup'>
  ): Promise<void> => {
    return createTeam({
      siteDomain: subdomain,
      programId,
      team,
    })
      .unwrap()
      .then(({ id }: { id: string }) => {
        selectTeam(programId, selectIndex, id);
      });
  };

  const isTabletLandscapeUp = useMediaQuery(
    breakpointQueries.tabletLandscapeUp
  );

  const { name, startDate, endDate, location, divisions } = tournament;

  const isCartPurged = divisions.some((division) => division.isCartPurged);

  const { divisionsStartDate, divisionsEndDate } = useMemo(() => {
    let divisionsStartDate: DateTime | undefined;
    let divisionsEndDate: DateTime | null = null;

    divisions.forEach((division) => {
      if (
        divisionsStartDate === undefined ||
        DateTime.fromISO(division.startDate) < divisionsStartDate
      ) {
        divisionsStartDate = DateTime.fromISO(division.startDate);
      }

      if (
        division.endDate !== null &&
        division.endDate !== undefined &&
        (divisionsEndDate === null ||
          DateTime.fromISO(division.endDate) > divisionsEndDate)
      ) {
        divisionsEndDate = DateTime.fromISO(division.endDate);
      }
    });

    return { divisionsStartDate, divisionsEndDate };
  }, [divisions]);

  // Maps price adjustment amounts to saved teams and groups them by division
  const { teamPriceAdjustments, totalPriceAdjustmentAmount } = useMemo(() => {
    return getTeamPriceAdjustmentsMapAndAmounts(
      divisions,
      divisionCardsState,
      teams
    );
  }, [divisionCardsState, divisions, teams]);

  const assembleUpdateRegistrationsPayload = () => {
    const registrations: UpdateRegistrationsPayload[] = [];
    for (const [programId, division] of Object.entries(divisionCardsState)) {
      const registration: UpdateRegistrationsPayload = {
        programId,
        teams:
          division.value === 'saved'
            ? division.savedTeams.map((id) => ({
                id,
                name: teams.find((team) => team.id === id)?.name ?? '',
              }))
            : [],
      };
      registrations.push(registration);
    }
    return registrations;
  };

  const handleSubmit = () => {
    const payload = assembleUpdateRegistrationsPayload();
    setIsLoadingCartRegistrations(true);
    updateRegistrations({
      siteDomain: subdomain,
      programId: tournament.id,
      editURL: `/app/tournaments/${tournament.id}/registration`,
      registrations: payload,
      includeRegistrationTriageFix: registrationTriageFix,
    })
      .then(() => {
        window.location.href = `https://${subdomain}.${sld}.${tld}/cartSummary`;
      })
      .catch(() => {
        setIsLoadingCartRegistrations(false);
      });
  };

  if (isUpdateRegistrationsLoading || isLoadingCartRegistrations) {
    const loadingMessage = hasRegistrationsToEdit
      ? UPDATE_CART_LOADING_MESSAGE
      : ADD_TO_CART_LOADING_MESSAGE;

    return (
      <MainContent centerContent={true}>
        <Loader loading description={loadingMessage} />
      </MainContent>
    );
  }

  const updateRegistrationsErrorMessage = hasRegistrationsToEdit
    ? UPDATE_CART_ERROR_MESSAGE
    : ADD_TO_CART_ERROR_MESSAGE;

  return (
    <MainContent>
      <WizardHeader
        endDate={divisionsEndDate ? divisionsEndDate : undefined}
        location={!tournament.hideLocationDetails ? location?.name : undefined}
        name={name}
        startDate={divisionsStartDate || DateTime.fromISO(startDate)}
      />
      <S.WizardContent>
        <ColumnGrid>
          <S.WizardAddDivisionsAndTeamsSection>
            {updateRegistrationsError ? (
              <ErrorCard message={updateRegistrationsErrorMessage} />
            ) : null}

            {submitButtonError ? (
              <ErrorCard
                message={
                  submitButtonError === 'unsaved-divisions'
                    ? submitUnsavedChangesErrorMessage
                    : SUBMISSION_NO_SELECTED_DIVISIONS_ERROR_MESSAGE
                }
              />
            ) : null}

            {hasDeleteTeamDivisionError ? (
              <ErrorCard message={DELETE_TEAM_DIVISION_ERROR_MESSAGE} />
            ) : null}

            <S.SectionTitle>{sectionTitle}</S.SectionTitle>

            {isCartPurged && showCartPurgeMessage ? (
              <div className={styles.alertContainer}>
                <Alert
                  message={getRegistrationsPurgedWarningMessage(
                    tournament.subProgramLabel
                  )}
                  icon={
                    <ExclamationCircleIcon size="xl" fill="var(--orange-700)" />
                  }
                  onClose={() => setShowCartPurgeMessage(false)}
                />
              </div>
            ) : null}

            <DivisionCards
              divisions={divisions}
              divisionCardsStateLookup={divisionCardsState}
              divisionToScrollToId={divisionToScrollToId}
              hideLocationDetails={tournament.hideLocationDetails}
              tournamentLocation={tournament.location}
              onTeamCreate={addTeam}
              onScrollToDivision={clearIdToScrollTo}
              onAddTeamSelect={addTeamSelect}
              onCancelDivisionChanges={cancelDivisionChanges}
              onClearDivision={clearDivision}
              onDiscardTeamSelect={discardTeamSelect}
              onEditDivision={editDivision}
              onSaveDivision={saveDivision}
              onSelectDivision={selectDivision}
              onSelectTeam={selectTeam}
              setHasDeleteTeamDivisionError={setHasDeleteTeamDivisionError}
              teamPriceAdjustments={teamPriceAdjustments}
              teams={teams}
              tournamentDates={{
                start: startDate,
                end: endDate,
              }}
              tournamentId={tournament.id}
              costLabel={tournament.costLabel}
            />
          </S.WizardAddDivisionsAndTeamsSection>
          {isTabletLandscapeUp ? (
            <SummaryAndSubmitLargerScreens
              divisionCardsState={divisionCardsState}
              onSubmissionErrors={submit}
              onSubmit={handleSubmit}
              submitButtonText={submitButtonText}
              teamPriceAdjustments={teamPriceAdjustments}
              teams={teams}
              totalPriceAdjustmentAmount={totalPriceAdjustmentAmount}
              tournament={tournament}
            />
          ) : null}
        </ColumnGrid>
      </S.WizardContent>
      {!isTabletLandscapeUp ? (
        <SummaryAndSubmitSmallerScreens
          divisionCardsState={divisionCardsState}
          onSubmissionErrors={submit}
          onSubmit={handleSubmit}
          submitButtonText={submitButtonText}
          teamPriceAdjustments={teamPriceAdjustments}
          teams={teams}
          totalPriceAdjustmentAmount={totalPriceAdjustmentAmount}
          tournament={tournament}
        />
      ) : null}
    </MainContent>
  );
}
/* */
